import clsx from 'clsx';
import React, { MouseEvent } from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import {
  BaseButtonProps,
  Intent,
  mapIntentToRefreshedIntent,
  RefreshedIntent,
} from './BaseButtonProps';
import { ButtonContent } from './ButtonContent';
import {
  bgColors,
  borderColors,
  colors,
  textColors,
} from '@teetsh/app/src/components/colors';
import { BgColor, BorderColor, TextColor } from '@teetsh/app/src/types/colors';
import transparentize from '@teetsh/app/src/utils/style/transparentize';
import { darken } from '@teetsh/app/src/utils/style/darken';

interface CProps {
  children?: any;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  'aria-label'?: string;
  type?: 'button' | 'submit';
  style?: object;
  onMouseDown?: (event: MouseEvent<HTMLElement>) => void;
  alignText?: 'center' | 'right' | 'left';
  title?: string;
}

export type Props = BaseButtonProps & CProps;

interface ButtonStyleProps {
  large?: boolean;
  // deprecated
  extralarge?: boolean;
  small?: boolean;
  outlined?: boolean;
  minimal?: boolean;
  active?: boolean;
  hasChildren?: boolean;
  alignText?: 'center' | 'right' | 'left';
  fill?: boolean;
  disabled?: boolean;
  disableIconColor?: boolean;
  loading?: boolean;
  intent?: Intent;
}

export const buttonCustomStyle = ({
  large,
  extralarge,
  small,
  hasChildren,
  alignText,
  fill,
  disabled,
  loading,
}: ButtonStyleProps) => [
  tw`rounded-lg inline-flex items-center justify-center leading-4 py-2 px-4 outline-none text-sm font-medium`,
  tw`outline-none!`,
  fill && tw`w-full`,
  alignText === 'left' && !loading && hasChildren && tw`justify-start`,
  alignText === 'right' && !loading && hasChildren && tw`justify-end`,
  (disabled || loading) && tw`cursor-not-allowed border-transparent`,
  css`
    min-width: 36px;
    min-height: 36px;
  `,
  large &&
    css(
      {
        minWidth: '42px',
        minHeight: '42px',
      },
      tw`leading-none text-base py-2`,
    ),
  extralarge &&
    css(
      {
        minWidth: '42px',
        minHeight: '42px',
      },
      tw`leading-none text-lg py-2`,
    ),
  small &&
    css(
      {
        minWidth: '32px',
        minHeight: '32px',
      },
      tw`leading-4 text-sm px-3 py-2`,
    ),
  // icon only paddings
  !hasChildren && [tw`p-1.5`, large && tw`p-2`, extralarge && tw`p-2.5`],
];

export const buttonColorsIntentStyle = ({
  intent = 'neutral',
  active,
  disabled,
  loading,
  filled,
  minimal,
  outlined,
}: {
  intent?: RefreshedIntent;
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  filled?: boolean;
  minimal?: boolean;
  outlined?: boolean;
}) => {
  const activeFilledStyle = css`
    background-color: ${darken(0.4, colors[`bg-${intent}-intense`])};
  `;
  const activeMinimalAndOutlinedStyle = css`
    background-color: ${transparentize(0.8, colors[`bg-${intent}-intense`])};
  `;
  return [
    // TEXT
    filled &&
      css`
        ${textColors('white')}
        ${bgColors(`bg-${intent}-intense` as BgColor)}
      `,
    minimal && textColors(`text-${intent}-moderate` as TextColor),

    // BACKGROUND
    filled &&
      !active &&
      !disabled &&
      !loading &&
      css`
        &:hover {
          background-color: ${darken(0.3, colors[`bg-${intent}-intense`])};
        }

        &:active {
          ${activeFilledStyle}
        }
      `,
    filled && active && activeFilledStyle,
    (outlined || minimal) && active && activeMinimalAndOutlinedStyle,
    outlined && textColors(`text-${intent}-intense` as TextColor),
    (outlined || minimal) &&
      !disabled &&
      !active &&
      !loading &&
      css`
        &:hover {
          background-color: ${transparentize(
            0.9,
            colors[`bg-${intent}-intense`],
          )};
        }

        &:active {
          ${activeMinimalAndOutlinedStyle}
        }
      `,
    // BORDER
    outlined &&
      css`
        ${tw`border`}
        ${borderColors(`border-${intent}-moderate` as BorderColor)}
      `,
    loading && tw`bg-opacity-50`,
    disabled && tw`opacity-40`,
  ];
};

export const buttonRootStyle = (p: ButtonStyleProps) => {
  const refreshedIntent = mapIntentToRefreshedIntent(p.intent);
  return [
    buttonCustomStyle(p),
    buttonColorsIntentStyle({
      ...p,
      intent: refreshedIntent,
      filled: !p.outlined && !p.minimal && refreshedIntent !== 'neutral',
      outlined: !p.minimal && (p.outlined || refreshedIntent === 'neutral'),
    }),
    p.alignText === 'right' && tw`text-right`,
    p.alignText === 'left' && tw`text-left`,
  ];
};

export default function Button(props: Props) {
  const {
    className,
    type,
    intent,
    alignText,
    active,
    loading,
    minimal,
    disabled,
    innerRef,
    children,
    outlined,
    extralarge,
    small,
    fill,
    large,
    iconSize,
    icon,
    rightIcon,
    onClick,
    onMouseDown,
    iconStyle,
    rightIconStyle,
    ...rest
  } = props;
  return (
    <button
      css={[
        buttonRootStyle({
          intent,
          hasChildren: !!children,
          active,
          alignText,
          disabled,
          extralarge,
          fill,
          large,
          loading,
          outlined,
          minimal,
          small,
        }),
      ]}
      ref={props.innerRef}
      type={type || 'button'}
      className={clsx('tsh-button', className)}
      disabled={disabled}
      onClick={e => {
        if (onClick && !disabled && !loading) {
          onClick(e);
        }
      }}
      onMouseDown={e => {
        if (onMouseDown && !disabled && !loading) {
          onMouseDown(e);
        }
      }}
      {...rest}
    >
      <ButtonContent {...props} />
    </button>
  );
}
